import { ethers } from 'ethers';
import { getCurrentNetwork } from '../../config/blockchain';
import { API_BASE_URL } from '../../api/config';

// ERC20 / BEP-20 token ABI (USDT, USDC)
const ERC20_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address account) view returns (uint256)",
  "function decimals() view returns (uint8)"
];

/**
 * Balance Manager for handling USDT and native token balances
 */
export class BalanceManager {
    constructor() {
    // Cache for USDT/USDC addresses to prevent continuous API calls
    this.cryptoAddressCache = new Map();
    this.balanceCheckInProgress = false;
    this.balanceCheckTimeouts = new Map();
  }

  /**
   * Get USDT balance with direct parameters (TIMING FIX)
   */
  async getUSDTBalanceWithParams(walletAddress, providerToUse, networkToUse, currentUsdtBalance = '0') {
    try {
      
      if (!providerToUse || !walletAddress) {
        return '0';
      }
      
      // Prevent concurrent balance checks for the same wallet
      if (this.balanceCheckInProgress) {
        return currentUsdtBalance; // Return current balance if check is in progress
      }
      
      this.balanceCheckInProgress = true;
      
      // Use same API base as rest of app (avoids HTML response when REACT_APP_BACKEND_URL was unset)
      const API_URL = API_BASE_URL || '';
      
      // Use getCurrentNetwork() to determine network based on environment
      const configuredNetwork = getCurrentNetwork();
      const isMainnet = configuredNetwork.chainId === 56;
      const networkParam = isMainnet ? 'mainnet' : 'testnet';
      
      
      // Check cache first to prevent continuous API calls
      const cacheKey = networkParam;
      const cachedData = this.cryptoAddressCache.get(cacheKey);
      const now = Date.now();
      
      let usdtAddress;
      const cacheAgeMs = cachedData ? (now - cachedData.timestamp) : null;
      const cacheValid = cacheAgeMs !== null && cacheAgeMs < 60000;
      const failureCacheValid = cacheAgeMs !== null && cacheAgeMs < 600000;

      if (cachedData && cacheValid && cachedData.usdtAddress) {
        usdtAddress = cachedData.usdtAddress;
      } else if (cachedData && cachedData.failed && failureCacheValid) {
        return '0';
      } else {
        const response = await fetch(`${API_URL}/api/wallet/usdt-address?network=${networkParam}`);
        if (!response.ok) {
          this.cryptoAddressCache.set(cacheKey, { failed: true, timestamp: now });
          return '0';
        }
        const contentType = response.headers.get('content-type') || '';
        const data = contentType.includes('application/json')
          ? await response.json()
          : {};
        if (typeof data !== 'object' || data === null) {
          this.cryptoAddressCache.set(cacheKey, { failed: true, timestamp: now });
          return '0';
        }
        if (!data.success || !data.usdtAddress) {
          this.cryptoAddressCache.set(cacheKey, { failed: true, timestamp: now });
          return '0';
        }
        usdtAddress = data.usdtAddress;
        this.cryptoAddressCache.set(cacheKey, {
          usdtAddress,
          usdcAddress: data.usdcAddress || null,
          timestamp: now
        });
      }
      
      const usdtContract = new ethers.Contract(usdtAddress, ERC20_ABI, providerToUse);
      
      const balance = await usdtContract.balanceOf(walletAddress);
      
      const formattedBalance = ethers.formatUnits(balance, 18);
      
      return formattedBalance;
      
    } catch (error) {
      return '0';
    } finally {
      this.balanceCheckInProgress = false;
    }
  }

  /**
   * Get USDT balance with debounce
   */
  async getUSDTBalance(walletAddress, provider, currentNetwork, currentUsdtBalance = '0') {
    // Debounce rapid successive calls
    const debounceKey = `${walletAddress}-${currentNetwork?.chainId}`;
    
    // Clear existing timeout for this wallet/network combo
    if (this.balanceCheckTimeouts.has(debounceKey)) {
      clearTimeout(this.balanceCheckTimeouts.get(debounceKey));
    }
    
    return new Promise((resolve) => {
      const timeoutId = setTimeout(async () => {
        this.balanceCheckTimeouts.delete(debounceKey);
        const balance = await this.getUSDTBalanceWithParams(walletAddress, provider, currentNetwork, currentUsdtBalance);
        resolve(balance);
      }, 500); // 500ms debounce
      
      this.balanceCheckTimeouts.set(debounceKey, timeoutId);
    });
  }

  /**
   * Get native token balance (BNB/ETH)
   */
  async getNativeBalance(walletAddress, provider) {
    try {
      if (!provider || !walletAddress) {
        return '0';
      }
      
      const balance = await provider.getBalance(walletAddress);
      return ethers.formatEther(balance);
    } catch (error) {
      return '0';
    }
  }

  /**
   * Send USDT transaction
   */
  async sendUSDTTransaction(signer, currentNetwork, to, amount) {
    try {
      if (!signer) throw new Error('No signer available');
      
      // Use same API base as rest of app
      const API_URL = API_BASE_URL || '';
      if (!API_URL) throw new Error('Backend URL not configured. Set REACT_APP_BACKEND_URL.');
      
      // Use getCurrentNetwork() to determine network based on environment
      const configuredNetwork = getCurrentNetwork();
      const isMainnet = configuredNetwork.chainId === 56;
      const networkParam = isMainnet ? 'mainnet' : 'testnet';
      
      const cacheKey = networkParam;
      const cachedData = this.cryptoAddressCache.get(cacheKey);
      const now = Date.now();
      
      let usdtAddress;
      if (cachedData && (now - cachedData.timestamp < 60000) && cachedData.usdtAddress) {
        usdtAddress = cachedData.usdtAddress;
      } else {
        const response = await fetch(`${API_URL}/api/wallet/usdt-address?network=${networkParam}`);
        const contentType = response.headers.get('content-type') || '';
        let data;
        if (contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text && text.trim().startsWith('<')) {
            throw new Error('Backend returned HTML instead of JSON. Check that REACT_APP_BACKEND_URL points to the API server.');
          }
          throw new Error('Failed to get USDT address from backend');
        }
        if (!data || !data.success) {
          throw new Error('Failed to get USDT address from backend');
        }
        usdtAddress = data.usdtAddress;
        this.cryptoAddressCache.set(cacheKey, {
          usdtAddress,
          usdcAddress: data.usdcAddress || null,
          timestamp: now
        });
      }
      
      const usdtContract = new ethers.Contract(usdtAddress, ERC20_ABI, signer);
      const tokenAmount = ethers.parseUnits(amount.toString(), 18);
      const tx = await usdtContract.transfer(to, tokenAmount);
      return tx;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send USDC transaction (BSC BEP-20). Mainnet only (usdcAddress from API).
   */
  async sendUSDCTransaction(signer, currentNetwork, to, amount) {
    try {
      if (!signer) throw new Error('No signer available');
      const API_URL = API_BASE_URL || '';
      if (!API_URL) throw new Error('Backend URL not configured. Set REACT_APP_BACKEND_URL.');
      
      const configuredNetwork = getCurrentNetwork();
      const isMainnet = configuredNetwork.chainId === 56;
      const networkParam = isMainnet ? 'mainnet' : 'testnet';
      
      const cacheKey = networkParam;
      const cachedData = this.cryptoAddressCache.get(cacheKey);
      const now = Date.now();
      
      let usdcAddress;
      if (cachedData && (now - cachedData.timestamp < 60000) && cachedData.usdcAddress) {
        usdcAddress = cachedData.usdcAddress;
      } else {
        const response = await fetch(`${API_URL}/api/wallet/usdt-address?network=${networkParam}`);
        const contentType = response.headers.get('content-type') || '';
        let data;
        if (contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text && text.trim().startsWith('<')) {
            throw new Error('Backend returned HTML instead of JSON. Check REACT_APP_BACKEND_URL.');
          }
          throw new Error('Failed to get USDC address from backend');
        }
        if (!data || !data.success) {
          throw new Error('Failed to get USDC address from backend');
        }
        usdcAddress = data.usdcAddress;
        if (!usdcAddress) {
          throw new Error('USDC deposits are not available on this network');
        }
        this.cryptoAddressCache.set(cacheKey, {
          usdtAddress: data.usdtAddress,
          usdcAddress: usdcAddress || null,
          timestamp: now
        });
      }
      
      const usdcContract = new ethers.Contract(usdcAddress, ERC20_ABI, signer);
      const tokenAmount = ethers.parseUnits(amount.toString(), 18);
      const tx = await usdcContract.transfer(to, tokenAmount);
      return tx;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Clear all caches and timeouts
   */
  cleanup() {
    this.cryptoAddressCache.clear();
    this.balanceCheckTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.balanceCheckTimeouts.clear();
    this.balanceCheckInProgress = false;
  }
}

// Create a singleton instance
export const balanceManager = new BalanceManager(); 